import { wccModules } from 'enums/wccModules';
import device from 'managers/device';
import { inject, injectable } from 'inversify';
import { Subscribable } from 'knockout';
import videojs from 'libraries/videojs';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import WCCVideo from 'models/attachments/video';
import { VideoJsPlayer } from 'video.js';
import './stage.html';
import './stage.scss';

const maxWidth = 1280;
const viewSizeX = 0.95;
const viewSizeY = 0.95;
const defaultRatio = 1.77;

@injectable()
export default class VideoOverlayStageVM {
    videoNode = ko.observable<HTMLElement>();

    isReady: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) video: WCCVideo,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const lqFile = video.lqFile;
        const hqFile = video.hqFile;

        const lqLink = lqFile.pluck(f => f.link);
        const hqLink = hqFile.pluck(f => f.link);

        const thumbnail = video.thumbnail;
        const thumbnailURL = thumbnail.pluck(th => th.link);

        const videoWidth = ko.observable(0);
        const videoHeight = ko.observable(0);

        const player = ko.observable<VideoJsPlayer>();

        const ratio = ko.pureComputed(() => {
            if (videoWidth() > 0 && videoHeight() > 0)
                return videoWidth() / videoHeight();

            return defaultRatio;
        });

        const playerSize = ko.pureComputed(() => {
            var currentRatio = ratio();

            var targetWidth = device.width() * viewSizeX,
                maxHeight = device.height() * viewSizeY;

            if (targetWidth > maxWidth)
                targetWidth = maxWidth;

            var targetHeight = targetWidth / currentRatio;

            if (targetHeight > maxHeight) {
                targetHeight = maxHeight;
                targetWidth = targetHeight * currentRatio;
            }

            return { width: targetWidth, height: targetHeight };
        });

        const playerWidth = playerSize.pluck(p => p.width);
        const playerHeight = playerSize.pluck(p => p.height);

        this.isReady = ko.pureComputed(() => videoWidth() > 0 && videoHeight() > 0);

        effects.register(node => {
            if (node != undefined) {
                const instance = videojs(node, {
                    controls: true,
                    preload: 'auto',
                    playbackRates: [0.5, 1, 1.5, 2],
                    controlBar: {
                        children: [
                            'playToggle',
                            'volumePanel',
                            'progressControl',
                            'playbackRateMenuButton',
                            'pictureInPictureToggle',
                            'qualitySelector',
                            'fullscreenToggle'
                        ]
                    }
                });

                instance.on('loadedmetadata', () => {
                    videoWidth(instance.videoWidth());
                    videoHeight(instance.videoHeight());

                    instance.on('waiting', () => instance.loadingSpinner.show());
                    instance.on('play', () => instance.loadingSpinner.hide());
                });

                ko.applyBindingsToNode((<any>instance).el_, {
                    'css': {
                        'video-overlay-stage__player': true
                    },

                    'style': {
                        'width': playerWidth.toPx(true),
                        'height': playerHeight.toPx(true)
                    }
                });

                player(instance);

                return [
                    withEffect((thumbnailURL) => {
                        if (thumbnailURL != undefined)
                            instance.poster(thumbnailURL);
                    }, [thumbnailURL], false),

                    withEffect((hqLink) => {
                        const sources = new Array<videojs.Tech.SourceObject>();

                        if (hqLink != undefined) {
                            sources.push(<any>{
                                src: hqLink,
                                label: 'HD',
                                type: 'video/mp4'
                            });
                        }

                        instance.src(sources);
                    }, [hqLink], false)
                ]
            } else {
                player(undefined);
            }
        }, [this.videoNode]);        

        ko.applyBindingsToNode(info.element, {
            'css': {
                'video-overlay-stage': true
            }
        });
    }
}